import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";

const maxRetries = 2;

const baseQuery = retry(
  createFlatBufferBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? ""),
  { maxRetries },
);

/** Arguments of `getDeletedBanks`; `orgId` is required, every other filter optional. */
export interface DeletedBanksQueryArgs {
  orgId: string;
  channelId?: string;
  serverId?: string;
  page?: number;
  limit?: number;
  token: string;
}

/** Arguments of `restoreBank`; `body` is omitted unless the port must be corrected. */
export interface RestoreBankArgs {
  id: string;
  body?: { logical_port?: number };
  token: string;
}

export const bankAPI = createApi({
  reducerPath: "bankAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [
    "tagGetBank",
    "tagGetBanks",
    "tagPostBank",
    "tagPutBank",
    "tagDeleteBank",
    "tagGetBanksLight",
    "tagGetDeletedBanks",
  ],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET BANK */
    getBank: builder.query({
      query: (body: any) => ({
        url: `/banks/${body.bankId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetBank"],
    }),
    //* *********************************************************** */
    //* GET ALL BANKS */
    getBanks: builder.query({
      query: (body: any) => ({
        url: `/banks/?&page=${body.page}&limit=${body.limit}${body.searchAndFilterString || ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetBanks"],
    }),
    //* *********************************************************** */
    //* GET DELETED BANKS */
    // Soft-deleted banks of one organization. Answers with the same
    // PaginatedBanks the live list does, so the schema-hash registry decodes it
    // and no new decoder exists; every row additionally carries `deletedAt`.
    // `org_id` is required by the server. Only banks whose channel is still
    // live are listed: a bank tombstoned by a channel or organization delete is
    // neither listed nor restorable.
    getDeletedBanks: builder.query({
      query: (body: DeletedBanksQueryArgs) => ({
        url: "/banks/deleted/",
        params: {
          org_id: body.orgId,
          channel_id: body.channelId,
          server_id: body.serverId,
          page: body.page,
          limit: body.limit,
        },
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetDeletedBanks"],
    }),
    //* *********************************************************** */
    //* GET ALL BANKS LIGHT */
    getBanksLight: builder.query({
      query: (body: any) => ({
        url: `/banks/light/${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetBanksLight"],
    }),
    //* *********************************************************** */
    //* GET ALL ORGANIZATION BANKS */
    getBanksByOrganization: builder.query({
      query: (body: any) => ({
        url: `/banks/org/?&page=${body.page}&limit=${body.limit}${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetBanks"],
    }),
    //* *********************************************************** */
    //* GET BANKS BY SERVER */
    getBanksByServer: builder.query({
      query: (body: any) => ({
        url: `/banks/byServer/${body.idServer}?&page=${body.page}&limit=${body.limit}${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetBanks"],
    }),
    //* *********************************************************** */
    //* CREATE BANK */
    postBank: builder.mutation({
      query: (body: any) => ({
        url: "/banks/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateBank" },
      invalidatesTags: ["tagGetBanks"],
    }),
    //* *********************************************************** */
    //* CREATE BANK ACTION */
    putBankAction: builder.mutation({
      query: (body: any) => ({
        url: `/banks/actions/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetBanks", "tagGetBank"],
    }),
    //* *********************************************************** */
    //* PUT BANK */
    putBank: builder.mutation({
      query: (body: any) => ({
        url: `/banks/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateBank" },
      invalidatesTags: ["tagGetBanks", "tagGetBank"],
    }),
    //* *********************************************************** */
    //* DELETE BANK */
    deleteBank: builder.mutation({
      query: (body: any) => ({
        url: `/banks/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
        responseHandler: "text",
      }),
      // A just-deleted bank must show up in the deleted list, which is otherwise
      // only ever fetched on demand.
      invalidatesTags: ["tagGetBanks", "tagGetDeletedBanks"],
    }),
    //* *********************************************************** */
    //* RESTORE A DELETED BANK */
    // Brings a soft-deleted bank and the children its delete cascaded to back.
    // The body is optional plain JSON `{ logical_port }`: it is sent only to
    // correct a port LiftCloud would refuse for the bank's adapter (LobbyVision2
    // accepts 0..255), so a bank stranded out of range is restorable in one call.
    // Like `putBankPinAssignments` it declares no request serializer, because no
    // FlatBuffer schema exists for this endpoint. The 200 answer is JSON too and
    // carries `not_restored`: what the delete removed for good (device and
    // data-block attachments) and the restore therefore cannot bring back.
    restoreBank: builder.mutation({
      query: (body: RestoreBankArgs) => ({
        url: `/banks/${body.id}/restore`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetBanks", "tagGetBank", "tagGetBanksLight", "tagGetDeletedBanks"],
    }),
    //* *********************************************************** */
    //* DELETE ONE BANK FLOOR */
    // Removes a single floor rather than shrinking the bank from the top via
    // floor_count. The server soft-deletes the floor and its lift stops,
    // resequences the surviving floors' position and decrements floor_count —
    // but deliberately leaves their floor_index alone, because that is the key
    // historical report rows are resolved through. Invalidates the bank tags so
    // the editor reloads the reconciled floor list rather than trusting local
    // state that no longer matches the server's.
    deleteBankFloor: builder.mutation({
      query: (body: any) => ({
        url: `/banks/${body.bankId}/floors/${body.floorId}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetBank", "tagGetBanks"],
    }),
    //* *********************************************************** */
    //* PUT BANK PIN ASSIGNMENTS */
    // Bank-scoped write-back for Sequent/Numato pin -> lift assignments.
    // Replaces the removed adapter-level `/cdoinput/:id` route (deleted from
    // LiftCloud Feb 2026) — pin assignments are written per-bank now, not
    // per-adapter, matching the fact that a single adapter's pin template can
    // be shared across several banks. Sends a plain JSON body: it declares no
    // `extraOptions.fbSerializer`, because no FlatBuffer schema exists for this
    // endpoint yet.
    putBankPinAssignments: builder.mutation({
      query: (body: any) => ({
        url: `/banks/${body.bankId}/pin-assignments`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetBank"],
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetBankQuery,
  useGetBankQuery,
  useLazyGetBanksQuery,
  useGetBanksLightQuery,
  useLazyGetBanksByServerQuery,
  useLazyGetBanksByOrganizationQuery,
  useLazyGetDeletedBanksQuery,
  usePostBankMutation,
  usePutBankMutation,
  useDeleteBankMutation,
  useRestoreBankMutation,
  useDeleteBankFloorMutation,
  usePutBankActionMutation,
  usePutBankPinAssignmentsMutation,
} = bankAPI;
